import React, { useMemo, useState } from "react";
import House from "../../../assets/House.png";
import Townhouse from "../../../assets/Townhouse.png";
import { CurrencySymbol } from "../../../utils/enums";

const PROPERTY_ROWS = [
  { type: "House", icon: House },
  { type: "Townhouse", icon: Townhouse },
  { type: "Duplex/Semi-detached", icon: Townhouse },
];

const toCount = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatSoldPrice = (value) => {
  const amount = toCount(value);
  if (amount === 0) return `${CurrencySymbol.NGN} 0`;
  if (amount >= 1_000_000) {
    return `${CurrencySymbol.NGN} ${(amount / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  }
  if (amount >= 10_000) {
    return `${CurrencySymbol.NGN} ${Math.round(amount / 1_000)}k`;
  }
  return `${CurrencySymbol.NGN} ${amount.toLocaleString("en-US")}`;
};

const formatRentPrice = (value) => {
  const amount = toCount(value);
  if (amount === 0) return `${CurrencySymbol.NGN} 0`;
  return `${CurrencySymbol.NGN} ${amount.toLocaleString("en-US")}`;
};

const normalizeType = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const findByPropertyType = (list, type) => {
  if (!Array.isArray(list)) return null;
  const target = normalizeType(type);
  return (
    list.find((item) => normalizeType(item?.propertyType) === target) ?? null
  );
};

const buildRows = (byPropertyType, isSoldTab) =>
  PROPERTY_ROWS.map((row) => {
    const apiItem = findByPropertyType(byPropertyType, row.type);
    return {
      type: row.type,
      icon: row.icon,
      sold: toCount(apiItem?.count),
      medianPrice: isSoldTab
        ? formatSoldPrice(apiItem?.medianPrice)
        : formatRentPrice(apiItem?.medianPrice),
      medianDays: toCount(apiItem?.medianDaysAdvertised),
    };
  });

const ProformanceCard = ({ AgencyData, MarketPerformanceData }) => {
  const [activeTab, setActiveTab] = useState("sold");

  const market = MarketPerformanceData ?? {};
  const summary = market?.summary ?? {};
  const soldSection = market?.sold ?? {};
  const leasedSection = market?.leased ?? {};
  const sales = market?.sales ?? {};
  const rent = market?.rent ?? {};

  const periodMonths = toCount(market?.periodMonths) || 12;
  const agencyName =
    market?.agencyName ||
    AgencyData?.[0]?.principal_name ||
    AgencyData?.[0]?.agency_name ||
    "this agency";

  const soldRows = useMemo(
    () => buildRows(soldSection?.byPropertyType, true),
    [soldSection?.byPropertyType],
  );

  const leasedRows = useMemo(
    () => buildRows(leasedSection?.byPropertyType ?? rent?.byPropertyType, false),
    [leasedSection?.byPropertyType, rent?.byPropertyType],
  );

  const soldTotal = toCount(
    summary?.propertiesSold ?? soldSection?.totalCount ?? sales?.propertiesSold,
  );
  const soldMedian = formatSoldPrice(
    soldSection?.medianPrice ?? sales?.medianSoldPrice,
  );

  const leasedTotal = toCount(
    summary?.propertiesLeased ??
    leasedSection?.totalCount ??
    rent?.propertiesLeased,
  );
  const leasedMedian = formatRentPrice(
    leasedSection?.medianPrice ?? rent?.medianRentPrice,
  );

  const data = activeTab === "sold" ? soldRows : leasedRows;
  const soldText = activeTab === "sold" ? "Sold" : "Leased";
  const priceText =
    activeTab === "sold" ? "Median price" : "Median weekly rent";

  const introText =
    activeTab === "sold"
      ? `In the last ${periodMonths} months agents at ${agencyName} have sold ${soldTotal} properties with a median sold price of ${soldMedian} on myrealestate-ng.com.`
      : `In the last ${periodMonths} months agents at ${agencyName} have leased ${leasedTotal} properties with a median weekly rent of ${leasedMedian} on myrealestate-ng.com.`;

  return (
    <div className="mx-auto p-6 bg-white rounded-2xl">
      <div className="flex items-center gap-1 mb-1 text-[#000929]">
        <h2 className="text-2xl font-medium">Our Market Performance</h2>
      </div>
      <p className="text-[#000929] mt-1 mb-4 text-base font-normal opacity-75 w-full md:w-[75%]">
        {introText}
      </p>

      <div className="flex gap-4 mb-4">
        <button
          type="button"
          onClick={() => setActiveTab("sold")}
          className={`border-[1.5px] border-[#EAEAEA] px-5 py-2 rounded-full text-base transition-colors ${activeTab === "sold"
            ? "border-[#000929] border-[2px] font-semibold"
            : "hover:border-[0.5px] hover:border-gray"
            }`}
        >
          Sold
        </button>
        <button
          type="button"
          onClick={() => setActiveTab("leased")}
          className={`border-[1.5px] border-[#EAEAEA] px-6 py-2 rounded-full text-base transition-colors ${activeTab === "leased"
            ? "border-[#000929] border-[2px] font-semibold"
            : "hover:border-[0.5px] hover:border-gray"
            }`}
        >
          Leased
        </button>
      </div>

      <div className="space-y-4">
        {data.map((item) => (
          <div
            key={item.type}
            className="font-light text-base text-[#000929] border border-[#E5E3E8] rounded-lg overflow-hidden"
          >
            <div className="flex flex-col md:flex-row w-full">
              <div className="w-full md:w-3/12 p-5 flex flex-col items-center justify-center">
                <img src={item.icon} alt="" className="md:w-20" />
                <div>{item.type}</div>
              </div>
              <div className="bg-[#F6F5F7] h-20 mt-4 w-0.5 hidden md:block" />
              <div className="w-full md:w-9/12 p-2 md:p-5 flex justify-around items-start gap-2">
                <div>
                  <div className="font-normal text-sm text-[#000929]">
                    {soldText}
                  </div>
                  <div className="font-medium text-base md:text-2xl">
                    {item.sold} {soldText}
                  </div>
                </div>
                <div>
                  <div className="font-normal text-sm text-[#000929]">
                    {priceText}
                  </div>
                  <div className="font-medium text-base md:text-2xl">
                    {item.medianPrice}
                  </div>
                </div>
                <div>
                  <div className="font-normal text-sm text-[#000929]">
                    Median days advertised
                  </div>
                  <div className="font-medium text-base md:text-2xl">
                    {item.medianDays} days
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProformanceCard;
